import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import QuizHeader from './components/QuizHeader';
import MultipleChoiceQuestion from './components/MultipleChoiceQuestion';
import CharacterMatchingQuestion from './components/CharacterMatchingQuestion';
import PronunciationQuestion from './components/PronunciationQuestion';
import FeedbackModal from './components/FeedbackModal';
import QuizNavigation from './components/QuizNavigation';
import QuizResults from './components/QuizResults';
import { quizService } from '../../services/quizService';
import { quizDifficultyService } from '../../services/quizDifficultyService';
import { awardQuizXP } from '../../services/avatarBuilderService';
import CelebrationModal from '../../components/CelebrationModal';
import useCelebration from '../../hooks/useCelebration';
import useToast from '../../hooks/useToast';
import ToastContainer from '../../components/ToastContainer';
import useSwipeGesture from '../../hooks/useSwipeGesture';

const QUIZ_TYPE_LABELS = {
  multiple_choice: 'Multiple Choice',
  character_matching: 'Character Matching',
  pronunciation: 'Pronunciation',
};

const QuizInterface = () => {
  const navigate = useNavigate();
  const { quizId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { celebration, triggerBadge, dismissCelebration } = useCelebration();
  const { toasts, toastBadge, toastAchievement, addToast, dismiss } = useToast();
  const quizContainerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Scene context from play-reading-interface
  const playId = searchParams?.get('playId') || null;
  const actNumber = searchParams?.get('act') ? parseInt(searchParams?.get('act'), 10) : null;
  const sceneNumber = searchParams?.get('scene') ? parseInt(searchParams?.get('scene'), 10) : null;
  const quizType = searchParams?.get('quizType') || null;
  
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [quizStartTime] = useState(Date.now());
  const [questionStartTimes, setQuestionStartTimes] = useState({});
  const [timePerQuestion, setTimePerQuestion] = useState({});
  const [performanceAnalytics, setPerformanceAnalytics] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [milestoneBadges, setMilestoneBadges] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [xpWriteStatus, setXpWriteStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  const currentQuestion = questions?.[currentQuestionIndex];
  const totalQuestions = questions?.length;
  const isAnswered = answers?.[currentQuestion?.id] !== undefined;

  const toggleFullscreen = useCallback(() => {
    const el = quizContainerRef?.current;
    if (!document.fullscreenElement) {
      el?.requestFullscreen?.()?.then(() => setIsFullscreen(true))?.catch(() => {});
    } else {
      document.exitFullscreen?.()?.then(() => setIsFullscreen(false))?.catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleSwipeLeft = useCallback(() => {
    if (isAnswered && currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else if (isAnswered && currentQuestionIndex === totalQuestions - 1) {
      submitQuizAndAnalyze();
    }
  }, [isAnswered, currentQuestionIndex, totalQuestions]);

  const handleSwipeRight = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);

  const { onTouchStart, onTouchEnd } = useSwipeGesture({
    onSwipeLeft: handleSwipeLeft,
    onSwipeRight: handleSwipeRight,
  });

  useEffect(() => {
    loadQuiz();
  }, [quizId, user, playId, actNumber, sceneNumber, quizType]);

  useEffect(() => {
    if (currentQuestion && !questionStartTimes?.[currentQuestion?.id]) {
      setQuestionStartTimes(prev => ({
        ...prev,
        [currentQuestion?.id]: Date.now()
      }));
    }
  }, [currentQuestionIndex]);

  useEffect(() => {
    if (currentQuestion?.hasTimer && currentQuestion?.timerSeconds && !isAnswered) {
      setTimeRemaining(currentQuestion?.timerSeconds);
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleAnswer(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    } else {
      setTimeRemaining(null);
    }
  }, [currentQuestionIndex, isAnswered]);

  const loadQuiz = async () => {
    // If launched from play-reading-interface with scene context
    if (playId && actNumber && sceneNumber) {
      try {
        setLoading(true);
        setError('');

        // Try to find a quiz for this play/act/scene
        let targetQuizId = quizId;

        if (!targetQuizId) {
          // Look up quiz by play/act/scene
          const sceneQuizzes = await quizService?.getQuizzesByScene?.(playId, actNumber, sceneNumber);
          if (sceneQuizzes && sceneQuizzes?.length > 0) {
            targetQuizId = sceneQuizzes?.[0]?.id;
          }
        }

        if (targetQuizId) {
          const analytics = await quizDifficultyService?.getPerformanceAnalytics(user?.id, targetQuizId);
          setPerformanceAnalytics(analytics);

          const adaptiveQuestions = await quizDifficultyService?.getAdaptiveQuestions(
            user?.id,
            targetQuizId,
            analytics?.recommended_difficulty
          );

          let allQuestions = adaptiveQuestions?.length > 0
            ? adaptiveQuestions
            : (await quizService?.getQuizById(targetQuizId))?.quiz_questions || [];

          // Filter by quiz type if specified
          if (quizType && allQuestions?.length > 0) {
            const filtered = allQuestions?.filter(q => q?.question_type === quizType);
            allQuestions = filtered?.length > 0 ? filtered : allQuestions;
          }

          setQuestions(allQuestions);
        } else {
          // No quiz found for this scene — show a friendly message
          setError(`No quiz available for Act ${actNumber}, Scene ${sceneNumber} yet. Check back soon!`);
        }
      } catch (err) {
        setError(err?.message || 'Failed to load quiz');
        console.error('Error loading quiz:', err);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Original flow: load by quizId param
    if (!user || !quizId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const analytics = await quizDifficultyService?.getPerformanceAnalytics(user?.id, quizId);
      setPerformanceAnalytics(analytics);

      const adaptiveQuestions = await quizDifficultyService?.getAdaptiveQuestions(
        user?.id,
        quizId,
        analytics?.recommended_difficulty
      );

      if (adaptiveQuestions && adaptiveQuestions?.length > 0) {
        let qs = adaptiveQuestions;
        if (quizType) {
          const filtered = qs?.filter(q => q?.question_type === quizType);
          qs = filtered?.length > 0 ? filtered : qs;
        }
        setQuestions(qs);
      } else {
        const quizData = await quizService?.getQuizById(quizId);
        setQuiz(quizData);
        let qs = quizData?.quiz_questions || [];
        if (quizType) {
          const filtered = qs?.filter(q => q?.question_type === quizType);
          qs = filtered?.length > 0 ? filtered : qs;
        }
        setQuestions(qs);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load quiz');
      console.error('Error loading quiz:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateTimeForQuestion = (questionId) => {
    const startTime = questionStartTimes?.[questionId];
    if (!startTime) return 0;
    return Math.floor((Date.now() - startTime) / 1000);
  };

  const handleAnswer = async (isCorrect) => {
    const timeSpent = calculateTimeForQuestion(currentQuestion?.id);
    
    setTimePerQuestion(prev => ({
      ...prev,
      [currentQuestion?.id]: timeSpent
    }));

    const newAnswers = { ...answers, [currentQuestion?.id]: isCorrect };
    setAnswers(newAnswers);

    await quizDifficultyService?.updateQuestionMetrics(
      currentQuestion?.id,
      quizId,
      isCorrect,
      timeSpent
    );

    const encouragementMessages = {
      correct: [
        "Brilliant! Your understanding of Shakespeare is impressive!",
        "Excellent work! You\'re mastering the Bard\'s language!",
        "Outstanding! Keep up this fantastic progress!",
        "Superb! Your knowledge is truly remarkable!"
      ],
      incorrect: [
        "Don\'t worry! Every mistake is a learning opportunity!",
        "Keep going! You\'re building your Shakespeare expertise!",
        "Nice try! Review the material and you\'ll get it next time!",
        "Good effort! Learning takes practice and patience!"
      ]
    };

    const messages = isCorrect ? encouragementMessages?.correct : encouragementMessages?.incorrect;
    const randomMessage = messages?.[Math.floor(Math.random() * messages?.length)];

    const badgeUnlocked = isCorrect && Math.random() > 0.7 ? "Quick Learner" : null;

    setFeedbackData({
      isCorrect,
      xpEarned: isCorrect ? currentQuestion?.points : Math.floor(currentQuestion?.points * 0.3),
      badgeUnlocked,
      encouragementMessage: randomMessage
    });

    // Trigger badge celebration if a badge was unlocked mid-quiz
    if (badgeUnlocked) {
      triggerBadge({ name: badgeUnlocked, description: 'Earned during quiz' });
    }

    setShowFeedback(true);
  };

  const handleContinueFeedback = () => {
    setShowFeedback(false);
    setFeedbackData(null);

    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      submitQuizAndAnalyze();
    }
  };

  const submitQuizAndAnalyze = async () => {
    if (isSubmitting) return; // prevent double-click
    try {
      setIsSubmitting(true);
      setAnalysisLoading(true);
      
      const totalTime = Math.floor((Date.now() - quizStartTime) / 1000);
      const correctCount = Object.values(answers)?.filter(Boolean)?.length;

      const result = await quizService?.submitQuizAttempt(
        quizId,
        answers,
        totalTime,
        timePerQuestion
      );

      // ── Award XP to Supabase ──────────────────────────────────────────────
      if (user?.id) {
        // Compute XP using the same logic as calculateResults
        const earnedXP = questions?.reduce((sum, q) => {
          const isCorrect = answers?.[q?.id];
          return sum + (isCorrect ? q?.points : Math.floor(q?.points * 0.3));
        }, 0);

        // Determine quiz-specific badges
        const quizBadges = [];
        if (correctCount === questions?.length) {
          quizBadges?.push({ name: 'Perfect Score', description: 'Answered all questions correctly' });
        }
        if (correctCount >= Math.ceil(questions?.length * 0.8)) {
          quizBadges?.push({ name: 'Shakespeare Scholar', description: 'Achieved 80% or higher' });
        }

        try {
          setXpWriteStatus('saving');
          const { milestoneBadges: unlocked } = await awardQuizXP(user?.id, earnedXP, quizBadges);
          setMilestoneBadges(unlocked || []);
          setXpWriteStatus('saved');
          // Fire toast for each quiz badge earned
          quizBadges?.forEach((b) => toastBadge(b?.name));
          // Fire toast for each XP milestone badge unlocked
          unlocked?.forEach((b) => {
            addToast({
              type: 'achievement',
              title: '🎖️ XP Milestone Reached!',
              message: `You unlocked "${b?.name}" — ${b?.description || b?.desc || ''}`,
              duration: 5500,
            });
          });
        } catch (xpErr) {
          console.error('Failed to award quiz XP:', xpErr);
          setXpWriteStatus('error');
        }
      }
      // ─────────────────────────────────────────────────────────────────────

      const previousAttempts = await quizService?.getUserQuizAttempts(user?.id, quizId);
      
      const analysisData = {
        attemptId: result?.attemptId,
        score: correctCount,
        totalQuestions: questions?.length,
        timeTaken: totalTime,
        answers,
        timePerQuestion,
        previousAttempts: previousAttempts?.length - 1,
        averageScore: performanceAnalytics?.average_score,
        performanceTrend: performanceAnalytics?.difficulty_trend
      };

      const { analysis } = await quizDifficultyService?.analyzeQuizAttempt(
        user?.id,
        quizId,
        analysisData
      );

      setPerformanceAnalytics({
        ...performanceAnalytics,
        aiAnalysis: analysis
      });

      setShowResults(true);
    } catch (err) {
      console.error('Error submitting quiz:', err);
      setError('Failed to submit quiz. Please try again.');
    } finally {
      setAnalysisLoading(false);
      setIsSubmitting(false);
    }
  };

  const calculateResults = () => {
    const correctAnswers = Object.values(answers)?.filter(Boolean)?.length;
    const totalXP = questions?.reduce((sum, q, idx) => {
      const isCorrect = answers?.[q?.id];
      return sum + (isCorrect ? q?.points : Math.floor(q?.points * 0.3));
    }, 0);

    const badgesEarned = [];
    if (correctAnswers === totalQuestions) {
      badgesEarned?.push({ name: "Perfect Score", description: "Answered all questions correctly" });
    }
    if (correctAnswers >= Math.ceil(totalQuestions * 0.8)) {
      badgesEarned?.push({ name: "Shakespeare Scholar", description: "Achieved 80% or higher" });
    }

    const recommendations = performanceAnalytics?.aiAnalysis?.improvementSuggestions || [
      "Continue reading to deepen your understanding",
      "Practice with more quizzes to build confidence",
      "Review character relationships and motivations"
    ];

    return { 
      correctAnswers, 
      totalXP, 
      badgesEarned, 
      recommendations,
      aiInsights: performanceAnalytics?.aiAnalysis,
      recommendedDifficulty: performanceAnalytics?.aiAnalysis?.recommendedDifficulty
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg">Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <p className="text-red-500 mb-4">{error}</p>
          {playId && actNumber && sceneNumber ? (
            <button
              onClick={() => navigate(`/play-reading-interface?playId=${playId}`)}
              className="px-4 py-2 bg-primary text-white rounded hover:opacity-90"
            >
              Back to Play
            </button>
          ) : (
            <button
              onClick={() => navigate('/student-stage-dashboard')}
              className="px-4 py-2 bg-primary text-white rounded hover:opacity-90"
            >
              Back to Dashboard
            </button>
          )}
        </div>
      </div>
    );
  }

  if (showResults) {
    const results = calculateResults();
    return (
      <>
        <RoleBasedNavigation />
        <div className="main-content">
          <QuizResults
            totalQuestions={totalQuestions}
            correctAnswers={results?.correctAnswers}
            totalXP={results?.totalXP}
            badgesEarned={[...(results?.badgesEarned || []), ...(milestoneBadges || [])]}
            recommendations={results?.recommendations}
            aiInsights={results?.aiInsights}
            recommendedDifficulty={results?.recommendedDifficulty}
            userId={user?.id}
            userName={user?.user_metadata?.full_name || user?.email}
            onBadgeCelebrate={(badge) => {
              triggerBadge(badge);
              toastBadge(badge?.name);
            }}
          />
        </div>
        <CelebrationModal celebration={celebration} onDismiss={dismissCelebration} />
        <ToastContainer toasts={toasts} onDismiss={dismiss} />
      </>
    );
  }

  return (
    <>
      <RoleBasedNavigation />
      <div
        ref={quizContainerRef}
        className="main-content bg-background min-h-screen flex flex-col"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <QuizHeader
          currentQuestion={currentQuestionIndex + 1}
          totalQuestions={totalQuestions}
          xpAtStake={currentQuestion?.points}
          timeRemaining={timeRemaining}
          isFullscreen={isFullscreen}
          onToggleFullscreen={toggleFullscreen}
          onExit={() => {
            if (playId && actNumber && sceneNumber) {
              navigate(`/play-reading-interface?playId=${playId}`);
            } else {
              navigate('/student-stage-dashboard');
            }
          }}
        />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-4 sm:p-6 md:p-8">
            {/* Scene context banner */}
            {playId && actNumber && sceneNumber && (
              <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg flex items-center gap-3 flex-wrap">
                <span className="text-sm text-primary font-medium">
                  📖 Act {actNumber}, Scene {sceneNumber}
                </span>
                {quizType && (
                  <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full font-medium">
                    {QUIZ_TYPE_LABELS?.[quizType] || quizType}
                  </span>
                )}
              </div>
            )}

            {performanceAnalytics?.recommended_difficulty && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  📊 Difficulty Level: <strong>{performanceAnalytics?.recommended_difficulty?.toUpperCase()}</strong>
                  {' '}(Based on your performance)
                </p>
              </div>
            )}

            {/* Swipe hint for mobile */}
            <div className="flex items-center justify-center gap-2 mb-4 sm:hidden">
              <span className="text-xs text-muted-foreground">← Swipe to navigate →</span>
            </div>

            {currentQuestion?.question_type === 'multiple_choice' && (
              <MultipleChoiceQuestion
                question={currentQuestion?.question_text}
                options={currentQuestion?.options}
                correctAnswer={currentQuestion?.correct_answer}
                onAnswer={handleAnswer}
                isAnswered={isAnswered}
                lineContext={currentQuestion?.line_context || currentQuestion?.context || ''}
              />
            )}

            {currentQuestion?.question_type === 'character_matching' && (
              <CharacterMatchingQuestion
                characters={currentQuestion?.characters}
                descriptions={currentQuestion?.descriptions}
                correctMatches={currentQuestion?.correct_answer}
                onAnswer={handleAnswer}
                isAnswered={isAnswered}
              />
            )}

            {currentQuestion?.question_type === 'pronunciation' && (
              <PronunciationQuestion
                line={currentQuestion?.question_text}
                audioUrl={currentQuestion?.audio_url}
                characterName={currentQuestion?.character_name || 'Character'}
                playName={currentQuestion?.play_name || quiz?.title || 'Shakespeare'}
                onAnswer={handleAnswer}
                isAnswered={isAnswered}
              />
            )}
          </div>
        </div>

        <QuizNavigation
          currentQuestion={currentQuestionIndex + 1}
          totalQuestions={totalQuestions}
          canGoBack={currentQuestionIndex > 0}
          canGoNext={isAnswered}
          onPrevious={() => setCurrentQuestionIndex(prev => prev - 1)}
          onNext={() => {
            if (currentQuestionIndex < totalQuestions - 1) {
              setCurrentQuestionIndex(prev => prev + 1);
            } else {
              submitQuizAndAnalyze();
            }
          }}
          isAnswered={isAnswered}
          isSubmitting={isSubmitting}
        />

        {showFeedback && feedbackData && (
          <FeedbackModal
            isCorrect={feedbackData?.isCorrect}
            xpEarned={feedbackData?.xpEarned}
            badgeUnlocked={feedbackData?.badgeUnlocked}
            encouragementMessage={feedbackData?.encouragementMessage}
            onContinue={handleContinueFeedback}
          />
        )}

        {analysisLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg text-center min-w-[220px]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              {xpWriteStatus === 'saving' && (
                <p className="text-sm font-medium text-primary mb-1">Saving your XP & badges…</p>
              )}
              {xpWriteStatus === 'saved' && (
                <p className="text-sm font-medium text-success mb-1">✓ XP saved!</p>
              )}
              {xpWriteStatus === 'error' && (
                <p className="text-sm font-medium text-destructive mb-1">XP save failed — retrying…</p>
              )}
              <p className="text-muted-foreground">Analyzing your performance with AI...</p>
            </div>
          </div>
        )}

        <CelebrationModal celebration={celebration} onDismiss={dismissCelebration} />
      </div>
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </>
  );
};

export default QuizInterface;